import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ScrollToHash } from "@/components/site/ScrollToHash";
import { Toaster } from "@/components/ui/sonner";
import HomeV1 from "@/pages/HomeV1";
import HomeV2 from "@/pages/HomeV2";
import HomeV3 from "@/pages/HomeV3";
import HomeV4 from "@/pages/HomeV4";
import HomeFinal from "@/pages/HomeFinal";
import HistoryPage from "@/pages/HistoryPage";
import WhereToBuyPage from "@/pages/WhereToBuyPage";
import SupportPage from "@/pages/SupportPage";
import WhoWeArePage from "@/pages/WhoWeArePage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import ProgramsPage from "@/pages/ProgramsPage";
import FlatsMentorFarmPage from "@/pages/FlatsMentorFarmPage";
import MentorshipProgramPage from "@/pages/MentorshipProgramPage";
import ImpactPage from "@/pages/ImpactPage";
import MeetTheFarmersPage from "@/pages/MeetTheFarmersPage";
import MediaPage from "@/pages/MediaPage";
import ProgramLandsitesPage from "@/pages/ProgramLandsitesPage";
import FarmerPortalPage from "@/pages/FarmerPortalPage";
import FarmersMarketsPage from "@/pages/FarmersMarketsPage";
import CsaPage from "@/pages/CsaPage";
import WholesalePage from "@/pages/WholesalePage";
import CropsPage from "@/pages/CropsPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import WorkWithUsPage from "@/pages/WorkWithUsPage";

/**
 * AnimatedRoutes — wraps the route outlet in a short cross-fade keyed by
 * pathname so each navigation reads as a *new page* rather than a scroll jump.
 * Also forces an instant scroll-to-top on route change (no smooth animation),
 * unless the URL carries a #hash (handled by ScrollToHash).
 */
function AnimatedRoutes() {
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => {
    if (location.hash) return; // hash targets are handled by ScrollToHash
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={reduce ? undefined : { opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
        <Routes location={location}>
          <Route path="/" element={<HomeFinal />} />
          <Route path="/v1" element={<HomeV1 />} />
          <Route path="/v2" element={<HomeV2 />} />
          <Route path="/v3" element={<HomeV3 />} />
          <Route path="/v4" element={<HomeV4 />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/where-to-buy" element={<WhereToBuyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/who-we-are" element={<WhoWeArePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/impact" element={<ImpactPage />} />
          <Route path="/meet-the-farmers" element={<MeetTheFarmersPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/programs/flats-mentor-farm" element={<FlatsMentorFarmPage />} />
          <Route path="/programs/mentorship" element={<MentorshipProgramPage />} />
          <Route path="/program-landsites" element={<ProgramLandsitesPage />} />
          <Route path="/farmer-portal" element={<FarmerPortalPage />} />
          <Route path="/farmers-markets" element={<FarmersMarketsPage />} />
          <Route path="/csa" element={<CsaPage />} />
          <Route path="/wholesale" element={<WholesalePage />} />
          <Route path="/crops" element={<CropsPage />} />
          <Route path="/get-involved" element={<Navigate to="/support" replace />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:slug" element={<EventDetailPage />} />
          <Route path="/work-with-us" element={<WorkWithUsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<AdminPage />} />
          {/* Alternate designs remain accessible by direct URL:
              /v1 (Editorial), /v2 (Immersive), /v3 (Story), /v4 (Fresh).
              The floating VersionSwitcher was removed after design approval;
              re-add <VersionSwitcher /> here to bring it back if needed. */}
          <Route path="*" element={<HomeFinal />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToHash />
        <AnimatedRoutes />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
